import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useOrcamento } from './useOrcamento';
import { log } from '../utils/DebugLogger';
import { eventBus } from '../eventBus';

/**
 * Composable for handling the "Agendar" (Schedule) button action
 * This provides a single source of truth for scheduling an orcamento:
 * 1. Approve the orcamento (change status to "Aprovado")
 * 2. On success, redirect to the agenda page
 *
 * @returns {Object} - Object containing the handleAgendar function
 */
export function useAgendarOrcamento() {
  const router = useRouter();
  const { t } = useI18n();
  const { aprovarOrcamento } = useOrcamento();

  /**
   * Handle the "Agendar" button click
   * Approves the orcamento and redirects to agenda on success
   *
   * @param {number|string} orcamentoId - The ID of the orcamento to schedule
   * @returns {Promise<boolean>} - Returns true if successful, false otherwise
   */
  const handleAgendar = async (orcamentoId: number) => {
    log('useAgendarOrcamento', 'handleAgendar', 'Scheduling orcamento', orcamentoId);

    if (!orcamentoId) {
      log('useAgendarOrcamento', 'handleAgendar', 'No orcamento ID provided');
      eventBus.emit('globalMessage.error', t('ID do orçamento não encontrado'));
      return false;
    }

    try {
      // Approve the orcamento (changes status to "Aprovado")
      const response = await aprovarOrcamento(orcamentoId);

      if (response.success) {
        // Show success message
        eventBus.emit('globalMessage.success', t('Orçamento aprovado com sucesso'));

        // Redirect to agenda page after a brief delay
        setTimeout(() => {
          router.push('/agenda');
        }, 500);

        return true;
      } else {
        // Handle unsuccessful response
        const errorMsg = response.error || t('Erro ao aprovar orçamento');
        log('useAgendarOrcamento', 'handleAgendar', 'Failed', errorMsg);
        eventBus.emit('globalMessage.error', errorMsg);
        return false;
      }
    } catch (error) {
      // Handle exception
      log('useAgendarOrcamento', 'handleAgendar', 'Error', error);
      const errorMsg = error.message || t('Erro ao aprovar orçamento');
      eventBus.emit('globalMessage.error', errorMsg);
      return false;
    }
  };

  /**
   * O texto de cada motivo que a regra única do backend (AgendaDoOrcamento, chamado DS 2026-0068)
   * devolve para "não pode agendar" / "não está na agenda".
   */
  const motivoTexto = (motivo: string): string => {
    switch (motivo) {
      case 'cancelado': return t('O orçamento está cancelado');
      case 'concluido': return t('Todos os serviços já foram realizados');
      case 'nao_aprovado': return t('O orçamento ainda não foi aprovado');
      case 'sem_servico': return t('O orçamento não tem serviços');
      case 'sem_servico_pendente': return t('Não há serviço aguardando agendamento');
      case 'sem_rua': return t('Falta a rua no cadastro do cliente');
      case 'sem_numero': return t('Falta o número no cadastro do cliente');
      case 'sem_zona': return t('Falta a zona (Localidade) no cadastro do cliente');
      case 'fora_do_periodo': return t('O orçamento foi criado fora do período do filtro');
      default: return motivo;
    }
  };

  const motivosTexto = (motivos: string[] | undefined): string => (motivos ?? []).map(motivoTexto).join('; ');

  /** Os motivos que se resolvem completando o cadastro do cliente. */
  const faltaCadastroDoCliente = (motivos: string[] | undefined): boolean =>
    (motivos ?? []).some((m) => m === 'sem_rua' || m === 'sem_numero' || m === 'sem_zona');

  return {
    handleAgendar,
    motivoTexto,
    motivosTexto,
    faltaCadastroDoCliente
  };
}
